// src/components/Dealer/DealerInfoCard.tsx

import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { CreditCard, Download, Image as ImageIcon, User, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { type DealerImageType, dealerApi, type LicenseScanInfo } from '../../services/dealerApi';
import type { Dealer } from '../../types/dealer';
import DealerLicenseCheck from './DealerLicenseCheck';
import DealerStatusPanel from './DealerStatusPanel';
import { dealerStatusOf } from './dealerStatus';

// 종사원증 사진 판정 표시
export const LICENSE_VERDICTS: Record<NonNullable<LicenseScanInfo['verdict']>, { label: string; className: string }> = {
  match: { label: '일치', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  check: { label: '확인 필요', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  mismatch: { label: '불일치', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  unchecked: { label: '미확인', className: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
};

interface DealerInfoCardProps {
  dealer: Dealer;
}

export default function DealerInfoCard({ dealer }: DealerInfoCardProps) {
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // 종사원증: 지금 번호·상태, 지금 번호의 사진 판정(없으면 null), 확인 대기 요청
  const { data: licenseData, refetch: refetchLicense } = useQuery({
    queryKey: ['dealer-license-scan', dealer.id],
    queryFn: () => dealerApi.getLicenseScan(dealer.id),
  });
  const licenseScan = licenseData?.data?.scan ?? null;
  // 확인됨으로 바꾸면(교체 요청이면 사진도 바뀜) 이미지를 다시 받는다
  const [imagesVersion, setImagesVersion] = useState(0);
  const handleLicenseApproved = () => {
    refetchLicense();
    setImagesVersion((v) => v + 1);
    queryClient.invalidateQueries({ queryKey: ['dealer'] });
    // 확인됨 처리는 딜러 상태(확인 중 → 활성)와 변경 기록도 바꾼다
    queryClient.invalidateQueries({ queryKey: ['dealer-status-history', dealer.id] });
  };

  // 공개 주소가 없는 이미지(종사원증 사진·QR, 공개 주소 없는 프로필)는 API 에서 받아 blob URL 로 보여준다.
  // 서버가 토큰을 헤더로만 받아서 <img src> 에 API 주소를 바로 넣을 수 없다.
  const [apiImageUrls, setApiImageUrls] = useState<Partial<Record<DealerImageType, string>>>({});
  const [apiImagesLoading, setApiImagesLoading] = useState(true);

  // biome-ignore lint/correctness/useExhaustiveDependencies: imagesVersion 은 확인됨 처리 뒤 다시 받기 위한 값
  useEffect(() => {
    let cancelled = false;
    const created: string[] = [];
    const types: DealerImageType[] = ['license-scan', 'band-card'];
    if (!dealer.profile_image_original) types.push('profile');
    if (!dealer.profile_image) types.push('enhanced');

    setApiImagesLoading(true);
    Promise.all(types.map(async (type) => [type, await dealerApi.fetchImageObjectUrl(dealer.id, type)] as const)).then(
      (entries) => {
        const urls: Partial<Record<DealerImageType, string>> = {};
        for (const [type, url] of entries) {
          if (url) {
            created.push(url);
            urls[type] = url;
          }
        }
        if (cancelled) {
          for (const url of created) window.URL.revokeObjectURL(url);
          return;
        }
        setApiImageUrls(urls);
        setApiImagesLoading(false);
      },
    );

    return () => {
      cancelled = true;
      for (const url of created) window.URL.revokeObjectURL(url);
    };
  }, [dealer.id, dealer.profile_image, dealer.profile_image_original, imagesVersion]);

  // publicUrl 이 있으면 다운로드도 그 주소로, 없으면 API(헤더 인증)로 받는다
  const imageTypes = [
    {
      type: 'profile' as const,
      label: '원본 사진',
      icon: 'user',
      publicUrl: dealer.profile_image_original || undefined,
      url: dealer.profile_image_original || apiImageUrls.profile,
    },
    {
      type: 'enhanced' as const,
      label: '보정 사진',
      icon: 'sparkles',
      publicUrl: dealer.profile_image || undefined,
      url: dealer.profile_image || apiImageUrls.enhanced,
    },
    {
      type: 'license-scan' as const,
      label: '종사원증 사진',
      icon: 'card',
      publicUrl: undefined,
      url: apiImageUrls['license-scan'],
    },
    {
      type: 'band-card' as const,
      label: '밴드 카드',
      icon: 'band',
      publicUrl: undefined,
      url: apiImageUrls['band-card'],
    },
  ];

  // 상태는 아래 '딜러 상태'의 이용정지·해제 버튼으로 바꾼다
  const dealerStatus = dealerStatusOf(dealer.status);

  const defaultProfileImage =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150'%3E%3Crect width='150' height='150' fill='%23F3F4F6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%239CA3AF' font-size='12' font-family='sans-serif'%3ENo Image%3C/text%3E%3C/svg%3E";

  return (
    <>
      <div className='rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900'>
        {/* 헤더 */}
        <div className='border-b border-gray-100 p-5 dark:border-gray-700 lg:p-6'>
          <div className='flex items-center justify-between'>
            <h4 className='text-lg font-semibold text-gray-800 dark:text-white/90'>딜러 정보</h4>
          </div>
        </div>

        {/* 본문 */}
        <div className='p-5 lg:p-6'>
          {/* 프로필 섹션 */}
          <div className='mb-6 flex items-center gap-4'>
            <div className='h-12 w-12 overflow-hidden rounded-full border border-gray-200 dark:border-gray-700'>
              <img
                // 보정 → 원본 → 기본 이미지 순
                src={
                  dealer.profile_image ||
                  apiImageUrls.enhanced ||
                  dealer.profile_image_original ||
                  apiImageUrls.profile ||
                  defaultProfileImage
                }
                alt={dealer.name}
                className='h-full w-full object-cover'
                onError={(e) => {
                  e.currentTarget.src = defaultProfileImage;
                }}
              />
            </div>
            <div>
              <h3 className='font-semibold text-gray-900 dark:text-white'>{dealer.name}</h3>
              <p className='text-sm text-gray-500 dark:text-gray-400'>
                {dealer.company_name} · {dealer.dealer_complex}
              </p>
            </div>
            <span className={`ml-auto px-2 py-1 text-xs font-medium rounded-full ${dealerStatus.className}`}>
              {dealerStatus.label}
            </span>
          </div>

          {/* 정보 그리드 */}
          <div className='grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6'>
            <div>
              <p className='text-xs text-gray-500 dark:text-gray-400'>전화번호</p>
              <p className='font-medium text-gray-900 dark:text-white'>{dealer.phone}</p>
            </div>
            <div>
              <p className='text-xs text-gray-500 dark:text-gray-400'>가입일</p>
              <p className='font-medium text-gray-900 dark:text-white'>
                {dayjs(dealer.created_at).format('YYYY.MM.DD')}
              </p>
            </div>
            <div>
              <p className='text-xs text-gray-500 dark:text-gray-400'>최종 수정일</p>
              <p className='font-medium text-gray-900 dark:text-white'>
                {dayjs(dealer.updated_at).format('YYYY.MM.DD')}
              </p>
            </div>
          </div>

          {/* 딜러 이미지 (미리보기 + 다운로드) */}
          <div className='mt-6 border-t border-gray-200 pt-4 dark:border-gray-700'>
            <p className='text-xs text-gray-500 dark:text-gray-400 mb-3'>딜러 이미지</p>
            <div className='grid grid-cols-2 gap-4 sm:grid-cols-4'>
              {imageTypes.map((item) => {
                const IconComponent = item.icon === 'band' ? ImageIcon : item.icon === 'card' ? CreditCard : User;
                return (
                  <div key={item.type}>
                    <p className='mb-2 flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-300'>
                      <IconComponent className='h-3.5 w-3.5' />
                      {item.label}
                    </p>
                    <div
                      className='relative aspect-square cursor-pointer overflow-hidden rounded-lg border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800 group'
                      onClick={() => item.url && setPreviewImage(item.url)}>
                      {item.url && (
                        <img
                          src={item.url}
                          alt={item.label}
                          // 종사원증 사진은 가로로 긴 카드라 잘리지 않게 전체를 보여준다
                          className={`h-full w-full transition-transform duration-200 group-hover:scale-105 ${
                            item.type === 'license-scan' ? 'object-contain' : 'object-cover'
                          }`}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                            if (fallback) {
                              fallback.style.display = 'flex';
                            }
                          }}
                        />
                      )}
                      <div
                        className='hidden h-full w-full flex-col items-center justify-center text-gray-400'
                        style={{ display: item.url ? 'none' : 'flex' }}>
                        <IconComponent className='h-8 w-8' />
                        <p className='mt-1 text-xs'>
                          {!item.url && apiImagesLoading
                            ? '불러오는 중'
                            : item.type === 'license-scan'
                              ? licenseScan
                                ? '사진 파일 없음'
                                : '촬영 기록 없음'
                              : '없음'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        dealerApi.downloadImage(dealer.id, item.type, dealer.name, item.publicUrl);
                      }}
                      className='mt-2 inline-flex w-full items-center justify-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'>
                      <Download className='h-3 w-3' />
                      다운로드
                    </button>
                    {item.type === 'license-scan' && licenseScan && (
                      <div className='mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-400'>
                        {licenseScan.verdict && (
                          <span
                            className={`inline-block rounded px-1.5 py-0.5 font-medium ${LICENSE_VERDICTS[licenseScan.verdict]?.className ?? ''}`}>
                            {LICENSE_VERDICTS[licenseScan.verdict]?.label ?? licenseScan.verdict}
                          </span>
                        )}
                        <p>촬영 때 입력한 번호 {licenseScan.verified_number || licenseScan.submitted_number || '-'}</p>
                        {licenseScan.attempts != null && <p>{licenseScan.attempts}번 촬영</p>}
                        {licenseScan.captured_at && (
                          <p>{dayjs(licenseScan.captured_at).format('YYYY.MM.DD HH:mm')} 촬영</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {licenseData?.data && (
            <DealerLicenseCheck
              license={licenseData.data}
              onApproved={handleLicenseApproved}
              onPreview={setPreviewImage}
            />
          )}

          <DealerStatusPanel dealer={dealer} hasPendingLicense={!!licenseData?.data?.pending} />

          {/* 주소 */}
          {dealer.address && (
            <div className='mt-6 border-t border-gray-200 pt-4 dark:border-gray-700'>
              <p className='text-xs text-gray-500 dark:text-gray-400 mb-1'>주소</p>
              <p className='text-sm text-gray-900 dark:text-white'>
                {dealer.address}
                {dealer.address_detail && ` ${dealer.address_detail}`}
              </p>
            </div>
          )}

          {/* 메모 */}
          {dealer.note && (
            <div className='mt-4 border-t border-gray-200 pt-4 dark:border-gray-700'>
              <p className='text-xs text-gray-500 dark:text-gray-400 mb-1'>메모</p>
              {/* 딜러가 쓴 글이라 HTML 로 넣지 않는다 (넣으면 소개글 속 스크립트가 관리자 화면에서 실행됨) */}
              <div className='text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap'>{dealer.note}</div>
            </div>
          )}
        </div>
      </div>

      {/* 이미지 미리보기 모달 */}
      {previewImage && (
        <div
          className='fixed inset-0 z-[9999] flex items-center justify-center bg-black/80'
          onClick={() => setPreviewImage(null)}>
          <button
            onClick={() => setPreviewImage(null)}
            className='absolute right-4 top-4 rounded-full bg-black/50 p-2 text-white hover:bg-black/70'>
            <X className='h-6 w-6' />
          </button>
          <img
            src={previewImage}
            alt='미리보기'
            className='max-h-[90vh] max-w-[90vw] rounded-lg object-contain'
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
